import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getProductById } from '../services/products';
import Button from '../components/Button';
import { useI18n } from '../context/I18nContext';

const StarRating = ({ rating, reviews }) => (
  <TranslatedStarRating rating={rating} reviews={reviews} />
);

const TranslatedStarRating = ({ rating, reviews }) => {
  const { t } = useI18n();

  return (
  <div className="flex items-center gap-2">
    <div className="flex">
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star} className={`text-lg ${star <= Math.floor(rating) ? 'text-amber-500' : 'text-gray-300'}`}>★</span>
      ))}
    </div>
    <span className="text-amber-700 font-semibold">{rating}</span>
    <span className="text-gray-500 text-sm">({t('product.reviews', { count: reviews })})</span>
  </div>
  );
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { t, translateCategory } = useI18n();
  const product = getProductById(id);

  const [selectedImg, setSelectedImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [imgError, setImgError] = useState(false);
  const [added, setAdded] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <span className="text-6xl">🐾</span>
        <h2 className="text-2xl font-bold text-gray-700">{t('product.productNotFound')}</h2>
        <Link to="/products"><Button>{t('product.backToShop')}</Button></Link>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="min-h-screen bg-amber-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6 flex gap-2">
          <Link to="/" className="hover:text-amber-700">{t('nav.home')}</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-amber-700">{t('nav.shop')}</Link>
          <span>/</span>
          <span className="text-amber-800 font-medium">{product.name}</span>
        </nav>

        <div className="bg-white rounded-2xl shadow-sm border border-amber-100 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Image Gallery */}
            <div className="p-6 bg-amber-50">
              <div className="mb-4 rounded-xl overflow-hidden bg-white h-80 md:h-96 flex items-center justify-center">
                {!imgError ? (
                  <img
                    src={product.images[selectedImg]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <span className="text-8xl">🐾</span>
                )}
              </div>
              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setSelectedImg(idx); setImgError(false); }}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImg === idx ? 'border-amber-700' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={t('product.imageView', { index: idx + 1 })} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-8">
              <span className="inline-block bg-amber-100 text-amber-800 text-xs font-medium px-3 py-1 rounded-full mb-4">
                {translateCategory(product.category)}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-serif text-gray-900 mb-4">{product.name}</h1>
              <StarRating rating={product.rating} reviews={product.reviews} />

              <div className="mt-6 mb-6">
                <span className="text-3xl font-bold text-amber-800">${product.price.toFixed(2)}</span>
                <span className="text-green-600 text-sm ml-3 font-medium">✓ {t('product.inStock')}</span>
              </div>

              <p className="text-gray-600 leading-relaxed mb-8">{product.description}</p>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('product.quantity')}</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    −
                  </button>
                  <span className="w-12 text-center font-bold text-lg text-gray-800">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-10 h-10 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 py-4 rounded-xl font-semibold text-base transition-all duration-200 ${
                    added
                      ? 'bg-green-500 text-white'
                      : 'bg-amber-700 hover:bg-amber-800 text-white'
                  }`}
                >
                  {added ? `✓ ${t('product.addedToCart')}` : t('product.addToCart')}
                </button>
                <Link to="/cart" className="flex-1">
                  <button className="w-full py-4 rounded-xl font-semibold text-base border-2 border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white transition-all duration-200">
                    {t('product.viewCart')}
                  </button>
                </Link>
              </div>

              {/* Features */}
              <div className="mt-8 pt-6 border-t border-amber-100 grid grid-cols-2 gap-4">
                {[
                  { icon: '🎨', label: t('product.features.handcrafted') },
                  { icon: '📦', label: t('product.features.securePackaging') },
                  { icon: '🚚', label: t('product.features.fastDelivery') },
                  { icon: '↩️', label: t('product.features.return30') },
                ].map(f => (
                  <div key={f.label} className="flex items-center gap-2 text-sm text-gray-600">
                    <span>{f.icon}</span>
                    <span>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Link to="/products">
            <Button variant="outline">← {t('product.backToCollection')}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
